import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { AiAgent } from 'src/app/models/AiAgent';
import { Campaign } from 'src/app/models/Campaign';
import { IntervalType } from 'src/app/models/IntervalType';
import { LeadList } from 'src/app/models/LeadList';
import { Sequence } from 'src/app/models/Sequence';
import { Tag } from 'src/app/models/Tag';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { AiAgentService } from 'src/app/shared/services/ai-agent.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { LeadVariable, MetaTemplate, MetaWhatsappService } from 'src/app/shared/services/meta-whatsapp.service';
import { SequencesService } from 'src/app/shared/services/sequences.service';
import { WhatsappAccount, WhatsappAccountsService } from 'src/app/shared/services/whatsapp-accounts.service';
import { QuickAgentModalComponent } from '../quick-agent-modal/quick-agent-modal.component';
import { accountStatusLabel, isAccountBroken, isAccountUsable } from 'src/app/shared/utils/whatsapp-account-status';
import {
  fillTemplateText, findTemplate, FIXED_PREFIX, isLegacyMapping, mappingSource, MY_LINK_LABEL, MY_LINK_TOKEN, normalizeTemplateMapping,
  TemplateMapping, TemplateSlot, templateBodyText, templateSlots,
} from 'src/app/shared/utils/whatsapp-template';
import { ownErrorMessage, ownErrorToastOptions } from 'src/app/shared/interceptors/http-error.interceptor';
import { BlacklistValidatorService } from 'src/app/shared/validators/blacklist.validator.service';

export { MY_LINK_TOKEN };

/** Ids de `interval_types`. A janela de follow-up só usa minuto e hora. */
export const MINUTE_INTERVAL = 1;
export const HOUR_INTERVAL = 2;

/** Teto da janela de follow-up: 23h59, em minutos (o mesmo do SaveSequenceRequest). */
export const FOLLOWUP_MAX_MINUTES = 1439;

/** Janela que o passo novo já traz, em horas — o padrão que o form da sequência já usava. */
export const FOLLOWUP_DEFAULT_WINDOW = 6;

/** Texto livre da Meta dentro da janela de 24h. */
export const WHATSAPP_TEXT_MAX = 4096;

export const WA_STEPS = ['Número', 'Template', 'Mensagem', 'Responder com IA', 'Resumo'];

/** Passo do Resumo: os lápis dos blocos levam ao passo de cada um e o "Continuar" volta para cá. */
export const SUMMARY_STEP = 5;

/** Valor do select de variável que abre o campo de texto fixo. */
export const FIXED_CHOICE = '__fixed__';

/** Agentes por página no passo "Responder com IA" (Bruno, 09/10: paginar acima de 5). */
export const AGENTS_PAGE_SIZE = 5;

/** Uma linha do "Integrações" do agente: número e o papel dele ali. */
export interface AgentIntegrationLine {
  name: string;
  phone?: string | null;
  note?: string;
}

/** Busca sem acento e sem caixa: "Joao" acha "João". */
function normalizeSearch(value: string): string {
  return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/**
 * Tags que o modal não sabe preencher: {cupom} e {url_checkout_cross} pedem campos do form antigo
 * que o passo de WhatsApp não tem (sairiam literais ou vazias para o lead), e {contentmsg}
 * substituiria a mensagem inteira.
 */
export const UNSUPPORTED_TAGS = ['{cupom}', '{url_checkout_cross}', '{contentmsg}'];

/** Dados de exemplo da prévia ao vivo do passo 3. */
const PREVIEW_SAMPLE: { [tag: string]: string } = {
  '{first_name}': 'João',
  '{email}': 'joao@exemplo.com',
  '{meu_link}': 'https://sf.link/s/3adox',
  '{cupom}': 'VOLTA10',
  '{list}': 'Carrinho abandonado',
};

/** Exemplo de cada dado do lead na prévia do template (o resto aparece como [rótulo]). */
const VARIABLE_SAMPLE: { [token: string]: string } = {
  first_name: 'João',
  name: 'João Pereira',
  email: 'joao@exemplo.com',
  [MY_LINK_TOKEN]: 'https://sf.link/s/3adox',
};

/**
 * Passo de WhatsApp da automação, em cinco passos (M4 — CU-17tm466mg5z, protótipo aprovado em 09/10).
 *
 * Primeiro o número (integração), depois o template DAQUELE número, a mensagem opcional que sai
 * quando o lead responde, quem responde (IA ou humano) e o resumo. A ordem é a do pedido do
 * Misael: templates e agentes dependem do número, então escolher o número primeiro evita
 * oferecer template ou agente que não sairia por ele.
 *
 * Editar um passo existente abre direto no resumo. Os passos do topo são clicáveis (na edição,
 * todos; na criação, os já vistos e o próximo, se os anteriores estão ok) e cada bloco do resumo
 * tem um lápis que leva ao passo dele — de lá, "Continuar" volta ao resumo (Bruno, 09/10).
 */
@Component({
  selector: 'app-whatsapp-sequence-modal',
  templateUrl: './whatsapp-sequence-modal.component.html',
  styleUrls: ['./whatsapp-sequence-modal.component.scss'],
})
export class WhatsappSequenceModalComponent implements OnInit, OnDestroy {
  @Input() campaign: Campaign;
  @Input() leadList: LeadList | null = null;
  @Input() intervalTypes: IntervalType[] = [];
  /** Passo existente (edição) ou nulo (novo). */
  @Input() sequence: Sequence | null = null;

  readonly steps = WA_STEPS;
  readonly textMax = WHATSAPP_TEXT_MAX;
  readonly fixedChoice = FIXED_CHOICE;
  step = 1;

  /** O passo mais adiante já aberto na criação: até ele o stepper deixa ir direto. */
  furthestStep = 1;

  /** Veio do resumo (lápis ou stepper): o "Continuar" volta para o resumo. */
  returnToSummary = false;

  /** Passos em que o "Continuar" já foi tentado: só aí os erros do passo aparecem. */
  attempted: { [step: number]: boolean } = {};

  accounts: WhatsappAccount[] = [];
  loadingAccounts = true;
  accountsError = false;

  templates: MetaTemplate[] = [];
  loadingTemplates = false;
  templatesError: string | null = null;

  leadVariables: LeadVariable[] = [];
  tags: Tag[] = [];

  agents: AiAgent[] = [];
  loadingAgents = false;
  agentsError = false;
  agentSearch = '';
  agentPage = 1;

  aiAvailable = false;
  saving = false;
  saveError: string | null = null;

  // O passo em edição. Propriedades soltas e não um FormGroup: cada passo valida um pedaço
  // diferente e o modal só grava no fim, então o FormGroup não compraria nada aqui.
  id: string | null = null;
  active = true;
  accountId: string | null = null;
  templateName: string | null = null;
  templateLanguage: string | null = null;
  /**
   * Variável do template → dado do lead (token), "Meu link" ou `text:<texto fixo>`. A chave é a
   * vaga (`body:nome`, `body:1`, `header:1`, `button:0`), a mesma que o back usa no envio.
   */
  templateVariables: { [key: string]: string } = {};
  /** Variáveis que o passo antigo não tinha e ganharam {first_name} por serem "de nome". */
  defaultedVariables: string[] = [];
  /** O mapeamento como veio do passo salvo; a lista antiga só vira mapa quando o template carrega. */
  private savedMapping: TemplateMapping = null;
  private mappingNormalized = true;
  followup = true;
  text = '';
  url = '';
  sendLeadNumber = false;
  followupWindow: number | null = FOLLOWUP_DEFAULT_WINDOW;
  followupWindowType = HOUR_INTERVAL;
  ai = false;
  agentId: string | null = null;
  interval: number | null = 30;
  intervalType = MINUTE_INTERVAL;

  private cursor = { start: 0, end: 0 };

  private subscriptions: Subscription[] = [];
  private templatesSub: Subscription | null = null;
  private agentsSub: Subscription | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private auth: AuthService,
    private toastService: ToastService,
    private whatsappAccounts: WhatsappAccountsService,
    private metaWhatsapp: MetaWhatsappService,
    private sequencesService: SequencesService,
    private aiAgentService: AiAgentService,
    private blacklistValidator: BlacklistValidatorService,
  ) {}

  ngOnInit(): void {
    this.aiAvailable = !!this.auth.getUser()?.ai_enabled;
    this.tags = this.sequencesService.getTags(this.leadList)
      .map(tag => ({ ...tag, tag: tag.tag.trim() }))
      .filter(tag => !UNSUPPORTED_TAGS.includes(tag.tag));

    if (this.sequence) {
      this.fromSequence(this.sequence);
      this.step = SUMMARY_STEP;
      this.furthestStep = SUMMARY_STEP;
    }

    this.loadAccounts();

    this.subscriptions.push(
      this.metaWhatsapp.leadVariables().subscribe({
        next: list => this.leadVariables = list || [],
        error: () => { /* sem catálogo o select fica só com o placeholder */ },
      })
    );

    if (this.accountId) {
      this.loadTemplates();
      this.loadAgents();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.templatesSub?.unsubscribe();
    this.agentsSub?.unsubscribe();
  }

  get isEditing(): boolean {
    return !!this.id;
  }

  /**
   * IA desligada na conta: o agente gravado fica no passo só enquanto a integração for a mesma —
   * trocar de número tira o agente do número antigo, como o selectAccount faz com a IA ligada
   * (review de 09/10 13:30).
   */
  get keepsSavedAgent(): boolean {
    const savedAccount = this.sequence?.meta_whatsapp_account_id || this.sequence?.whatsapp_account?.id || null;
    return !!this.sequence?.ai_agent_id && this.accountId === savedAccount;
  }

  // ------------------------------------------------------------------ passo 1 · número

  loadAccounts(): void {
    this.loadingAccounts = true;
    this.accountsError = false;
    this.subscriptions.push(
      this.whatsappAccounts.list().subscribe({
        next: res => {
          this.accounts = res?.data || [];
          this.loadingAccounts = false;

          // Um número só e pronto: o passo 1 já vem marcado, como pede o contrato da M3.
          const usable = this.accounts.filter(a => this.isSelectable(a));
          if (!this.accountId && usable.length === 1) {
            this.selectAccount(usable[0]);
          }
        },
        error: () => {
          this.loadingAccounts = false;
          this.accountsError = true;
        },
      })
    );
  }

  /** Pendente (sem número verificado) e desativada não enviam: aparecem, mas não dá para escolher. */
  isSelectable(account: WhatsappAccount): boolean {
    return isAccountUsable(account);
  }

  /** Token expirado/permissão negada: dá para salvar, mas nada sai até reconectar. */
  isBroken(account: WhatsappAccount | null): boolean {
    return isAccountBroken(account);
  }

  get selectedAccount(): WhatsappAccount | null {
    return this.accounts.find(a => a.id === this.accountId) || null;
  }

  get selectableAccounts(): WhatsappAccount[] {
    return this.accounts.filter(a => this.isSelectable(a));
  }

  statusLabel(account: WhatsappAccount): string {
    return accountStatusLabel(account);
  }

  accountHint(account: WhatsappAccount): string {
    if (account.status === 'pending') {
      return 'Sem número verificado e sem template — não dá para usar ainda';
    }
    if (account.status === 'disabled' || !account.active) {
      return 'Integração desativada — reative em Integrações WhatsApp';
    }

    const parts = [account.display_phone_number].filter(Boolean);
    const approved = account.templates_approved_count;
    if (approved !== null && approved !== undefined) {
      parts.push(`${approved} template${approved === 1 ? '' : 's'} aprovado${approved === 1 ? '' : 's'}`);
    }
    if (account.agents_count !== undefined) {
      parts.push(`${account.agents_count} agente${account.agents_count === 1 ? '' : 's'}`);
    }
    if (this.isBroken(account)) {
      parts.push('envios ficam aguardando até reconectar');
    }
    return parts.join(' · ');
  }

  selectAccount(account: WhatsappAccount): void {
    if (!this.isSelectable(account)) {
      return;
    }

    if (this.accountId !== account.id) {
      // Template e agente são do número: trocar o número invalida os dois.
      this.templateName = null;
      this.templateLanguage = null;
      this.resetVariables();
      this.agentId = null;
    }

    this.accountId = account.id;
    this.loadTemplates();
    this.loadAgents();
  }

  /**
   * "Adicionar outro número": só com mais de um número conectado (ou nenhum) — com um só, o
   * passo 1 já vem marcado e o botão só distraía (Bruno, 09/10). Os links do modal abrem em
   * nova aba para não perder o que o cliente preencheu.
   */
  get showAddNumber(): boolean {
    return !this.loadingAccounts && this.accounts.length > 0 && this.selectableAccounts.length !== 1;
  }

  // ------------------------------------------------------------------ passo 2 · template

  loadTemplates(): void {
    if (!this.accountId) {
      return;
    }

    this.templatesSub?.unsubscribe();
    this.loadingTemplates = true;
    this.templatesError = null;
    this.templatesSub = this.whatsappAccounts.listTemplates(this.accountId).subscribe({
      next: list => {
        this.templates = list || [];
        this.loadingTemplates = false;
        // Passo antigo sem idioma gravado (ou com outro): o template achado pelo nome vale, e o
        // select passa a mostrá-lo marcado em vez de "Selecione o template…".
        // Só para o passo sem idioma gravado: com idioma, o mesmo nome em outro idioma é outro
        // template (outros {{n}}), e trocar calado mudaria o que sai.
        const found = this.templateLanguage ? undefined : this.approvedTemplates.find(t => t.name === this.templateName);
        if (found?.language) {
          this.templateLanguage = found.language;
        }
        this.normalizeSavedMapping();
      },
      error: res => {
        this.templates = [];
        this.loadingTemplates = false;
        this.templatesError = res?.error?.message || 'A Meta recusou a listagem dos templates deste número.';
      },
    });
  }

  /**
   * Memoizado por `this.templates` (review de 10/10): um getter que filtra de novo a cada ciclo
   * de detecção cria uma array nova a cada vez, e o `*ngFor` do select recria as `<option>` — o
   * navegador pode perder a seleção (o template "salvo neste passo") no meio disso.
   */
  get approvedTemplates(): MetaTemplate[] {
    if (this.templates !== this.approvedTemplatesSource) {
      this.approvedTemplatesSource = this.templates;
      this.approvedTemplatesCache = this.templates.filter(t => t.status === 'APPROVED');
    }
    return this.approvedTemplatesCache;
  }

  private approvedTemplatesSource: MetaTemplate[] | undefined;
  private approvedTemplatesCache: MetaTemplate[] = [];

  get pendingTemplates(): MetaTemplate[] {
    if (this.templates !== this.pendingTemplatesSource) {
      this.pendingTemplatesSource = this.templates;
      this.pendingTemplatesCache = this.templates.filter(t => t.status === 'PENDING');
    }
    return this.pendingTemplatesCache;
  }

  private pendingTemplatesSource: MetaTemplate[] | undefined;
  private pendingTemplatesCache: MetaTemplate[] = [];

  /**
   * O template é nome + idioma: a Meta aprova o mesmo nome em mais de um idioma, e o envio usa
   * os dois. O select trabalha com a chave `nome|idioma`.
   */
  templateKey(template: MetaTemplate): string {
    return `${template.name}|${template.language}`;
  }

  get selectedTemplateKey(): string | null {
    return this.templateName ? `${this.templateName}|${this.templateLanguage}` : null;
  }

  /**
   * O template gravado no passo não está entre os aprovados que a Meta devolveu (saiu da lista,
   * ou a integração está sem token e nada veio): ele aparece marcado assim mesmo, com aviso.
   */
  get savedTemplateMissing(): boolean {
    return !!this.templateName && !this.approvedTemplates.some(t => this.templateKey(t) === this.selectedTemplateKey);
  }

  private findTemplate(name: string | null, language: string | null): MetaTemplate | undefined {
    return findTemplate(this.templates, name, language);
  }

  templateBody(name: string | null, language: string | null = this.templateLanguage): string {
    return templateBodyText(this.findTemplate(name, language));
  }

  /** O template escolhido, quando a lista do número carregou e ele está nela. */
  get selectedTemplate(): MetaTemplate | undefined {
    return this.findTemplate(this.templateName, this.templateLanguage);
  }

  get selectedBody(): string {
    return this.templateBody(this.templateName);
  }

  /**
   * As variáveis do template escolhido — nomeadas (`{{nome}}`) ou posicionais (`{{1}}`), do corpo,
   * do cabeçalho e dos botões. O mesmo `{{1}}` repetido é uma variável só para a Meta.
   */
  get slots(): TemplateSlot[] {
    const tpl = this.selectedTemplate;
    if (tpl !== this.slotsSource) {
      this.slotsSource = tpl;
      this.slotsCache = templateSlots(tpl);
    }
    return this.slotsCache;
  }

  private slotsSource: MetaTemplate | undefined;
  private slotsCache: TemplateSlot[] = [];

  /** Recebe a chave `nome|idioma` do select (ou só o nome). */
  selectTemplate(key: string | null): void {
    const [name, language] = (key || '').split('|');

    const tpl = name ? this.findTemplate(name, language || null) : undefined;
    const nextKey = tpl ? this.templateKey(tpl) : null;

    // Variáveis são do template: as de outro não valem aqui (um "Meu link" herdado pediria URL
    // sem o cliente ter escolhido).
    if (nextKey !== this.selectedTemplateKey) {
      this.resetVariables();
    }

    if (!tpl) {
      this.templateName = null;
      this.templateLanguage = null;
      this.resetVariables();
      return;
    }

    this.templateName = tpl.name;
    this.templateLanguage = tpl.language || 'pt_BR';

    // Só a variável de abertura nasce com o primeiro nome: o {{1}} e o {{nome}} (ou parecido).
    // As outras ficam vazias e o passo não avança até o cliente escolher.
    const next = { ...this.templateVariables };
    const { map } = normalizeTemplateMapping({}, this.slots);
    this.slots.forEach(slot => {
      if (!next[slot.key] && (map[slot.key] || (slot.component === 'body' && slot.id === '1'))) {
        next[slot.key] = map[slot.key] || 'first_name';
      }
    });
    this.templateVariables = next;
  }

  /** O que o select da variável mostra: o token, ou "Texto fixo". */
  slotChoice(slot: TemplateSlot): string {
    const value = this.templateVariables[slot.key];
    if (value === undefined || value === '') {
      return '';
    }
    return mappingSource(value).fixed ? FIXED_CHOICE : value;
  }

  slotFixedText(slot: TemplateSlot): string {
    const source = mappingSource(this.templateVariables[slot.key]);
    return source.fixed ? source.value : '';
  }

  /** Mídia do cabeçalho só aceita link fixo; o resto escolhe dado do lead, Meu link ou texto fixo. */
  isMediaSlot(slot: TemplateSlot): boolean {
    return ['image', 'video', 'document'].includes(slot.type);
  }

  setSlotChoice(slot: TemplateSlot, choice: string): void {
    const next = { ...this.templateVariables };
    if (choice === FIXED_CHOICE) {
      next[slot.key] = FIXED_PREFIX + this.slotFixedText(slot);
    } else if (choice) {
      next[slot.key] = choice;
    } else {
      delete next[slot.key];
    }
    this.templateVariables = next;
    this.defaultedVariables = this.defaultedVariables.filter(k => k !== slot.key);
  }

  setSlotFixed(slot: TemplateSlot, text: string): void {
    this.templateVariables = { ...this.templateVariables, [slot.key]: FIXED_PREFIX + (text || '') };
    this.defaultedVariables = this.defaultedVariables.filter(k => k !== slot.key);
  }

  isDefaulted(slot: TemplateSlot): boolean {
    return this.defaultedVariables.includes(slot.key);
  }

  variableLabel(token: string): string {
    if (token === MY_LINK_TOKEN) {
      return MY_LINK_LABEL;
    }
    const source = mappingSource(token);
    if (source.fixed) {
      return `“${source.value}”`;
    }
    return this.leadVariables.find(v => v.token === token)?.label || token;
  }

  /** O catálogo do lead mais o "Meu link" deste passo, que pode preencher uma variável (Decisão 7). */
  get variableOptions(): LeadVariable[] {
    // Recalcula só quando o catálogo muda: o select não recria as opções a cada ciclo.
    if (this.variableOptionsSource !== this.leadVariables) {
      this.variableOptionsSource = this.leadVariables;
      this.variableOptionsCache = this.leadVariables.some(v => v.token === MY_LINK_TOKEN)
        ? [...this.leadVariables]
        : [...this.leadVariables, { token: MY_LINK_TOKEN, label: MY_LINK_LABEL }];
    }
    return this.variableOptionsCache;
  }

  private variableOptionsSource: LeadVariable[] | null = null;
  private variableOptionsCache: LeadVariable[] = [];

  /** Exemplo do valor na prévia: texto fixo como está, dado conhecido com um exemplo, o resto como [rótulo]. */
  private previewValue(value: string): string {
    const source = mappingSource(value);
    if (source.fixed) {
      return source.value || '[texto fixo]';
    }
    return VARIABLE_SAMPLE[value] || `[${this.variableLabel(value)}]`;
  }

  /** Prévia do template com um exemplo de cada variável preenchida. */
  get templatePreview(): string {
    return fillTemplateText(this.selectedBody, 'body', this.templateVariables, v => this.previewValue(v));
  }

  get templateHeaderPreview(): string {
    const header = (this.selectedTemplate?.components || []).find(c => c.type === 'HEADER' && (c.format || 'TEXT') === 'TEXT');
    return header ? fillTemplateText(header.text, 'header', this.templateVariables, v => this.previewValue(v)) : '';
  }

  get templateButtons(): string[] {
    const buttons = (this.selectedTemplate?.components || []).find(c => c.type === 'BUTTONS')?.buttons || [];
    return buttons.map((b: any) => b.text || (b.type === 'COPY_CODE' ? 'Copiar código' : '')).filter(Boolean);
  }

  /** "{{nome}} = Primeiro nome · {{2}} = “Loja”" — o que preenche cada variável, para o resumo. */
  get variablesSummary(): { label: string; value: string }[] {
    if (this.selectedTemplate) {
      return this.slots.map(slot => ({ label: slot.label, value: this.templateVariables[slot.key] ? this.variableLabel(this.templateVariables[slot.key]) : '—' }));
    }
    // Sem a lista de templates: o que estava salvo — a lista antiga, item a item, ou o mapa.
    if (!this.mappingNormalized && Array.isArray(this.savedMapping)) {
      return this.savedMapping.map((token, i) => ({ label: `{{${i + 1}}}`, value: token ? this.variableLabel(token) : '—' }));
    }
    return Object.keys(this.templateVariables).map(key => ({ label: `{{${key.split(':')[1]}}}`, value: this.variableLabel(this.templateVariables[key]) }));
  }

  /** O template mudou: as variáveis do anterior não valem. */
  private resetVariables(): void {
    this.templateVariables = {};
    this.defaultedVariables = [];
    this.mappingNormalized = true;
  }

  /**
   * Passo salvo com a lista antiga (ou sem nada, o caso do #132000): vira mapa quando o template
   * carrega. A variável "de nome" sem dado ganha {first_name} — o mesmo que o envio faz — e fica
   * marcada para o cliente conferir.
   */
  private normalizeSavedMapping(): void {
    if (this.mappingNormalized || !this.selectedTemplate) {
      return;
    }
    const { map, defaulted } = normalizeTemplateMapping(this.savedMapping, this.slots);
    this.templateVariables = map;
    this.defaultedVariables = defaulted;
    this.mappingNormalized = true;
  }

  // ------------------------------------------------------------------ passo 3 · mensagem

  get usesLinkInTemplate(): boolean {
    return Object.values(this.templateVariables).includes(MY_LINK_TOKEN)
      || (!this.mappingNormalized && Array.isArray(this.savedMapping) && this.savedMapping.includes(MY_LINK_TOKEN));
  }

  get usesLinkInText(): boolean {
    return this.followup && (this.text || '').includes('{meu_link}');
  }

  /** O campo "Meu link" só existe quando alguém usa: a tag no texto ou a variável do template. */
  get needsLink(): boolean {
    return this.usesLinkInText || this.usesLinkInTemplate;
  }

  setCursor(event: any): void {
    this.cursor = { start: event.target.selectionStart, end: event.target.selectionEnd };
  }

  insertTag(tag: string): void {
    const text = this.text || '';
    const start = Math.min(this.cursor.start, text.length);
    const end = Math.min(this.cursor.end, text.length);
    const next = text.substring(0, start) + tag + text.substring(end);

    if (next.length > WHATSAPP_TEXT_MAX) {
      this.toastService.error('Não há espaço suficiente para inserir esta variável.');
      return;
    }

    this.text = next;
    this.cursor = { start: start + tag.length, end: start + tag.length };
  }

  get textPreview(): string {
    let text = this.text || '';
    Object.keys(PREVIEW_SAMPLE).forEach(tag => text = text.split(tag).join(PREVIEW_SAMPLE[tag]));
    return text;
  }

  get followupWindowInMinutes(): number {
    const window = Number(this.followupWindow) || 0;
    return Number(this.followupWindowType) === HOUR_INTERVAL ? window * 60 : window;
  }

  get followupIntervalTypes(): IntervalType[] {
    const types = (this.intervalTypes || []).filter(t => t.id === MINUTE_INTERVAL || t.id === HOUR_INTERVAL);
    return types.length ? types : [
      { id: MINUTE_INTERVAL, name: 'Minutos', type: 'minute' },
      { id: HOUR_INTERVAL, name: 'Horas', type: 'hour' },
    ];
  }

  // ------------------------------------------------------------------ passo 4 · IA

  loadAgents(): void {
    if (!this.accountId || !this.aiAvailable) {
      this.agents = [];
      return;
    }

    this.agentsSub?.unsubscribe();
    this.loadingAgents = true;
    this.agentsError = false;
    this.agentsSub = this.aiAgentService.listPaginated(1, 100, { whatsappAccountId: this.accountId, withUnassigned: true }).subscribe({
      next: res => {
        this.agents = res?.data || [];
        this.loadingAgents = false;
        this.agentPage = this.pageOfSelectedAgent();
        // Agente do passo que não veio (excluído, ou de outro número): sai da escolha, em vez de
        // seguir no payload com o resumo dizendo "desligado".
        if (this.agentId && !this.agents.some(a => a.id === this.agentId)) {
          this.agentId = null;
        }
      },
      // Falha não é "nenhum agente": não manda criar outro e não tira o agente do passo.
      error: () => {
        this.agents = [];
        this.loadingAgents = false;
        this.agentsError = true;
      },
    });
  }

  /**
   * Agente inativo não se escolhe (Bruno, 09/10): aparece desabilitado, com o selo. O que já
   * estava no passo e foi desligado em Meus Agentes continua marcado (editar o intervalo não pode
   * travar), mas não responde até ser ligado.
   */
  isAgentSelectable(agent: AiAgent): boolean {
    return !!agent.active;
  }

  agentStatusText(agent: AiAgent): string {
    if (agent.active) {
      return 'Ativo';
    }
    return agent.id === this.sequence?.ai_agent_id
      ? 'Inativo — ligue o agente em Meus Agentes para a IA responder'
      : 'Inativo — ative em Meus Agentes para escolher';
  }

  selectAgent(agent: AiAgent): void {
    if (this.isAgentSelectable(agent)) {
      this.agentId = agent.id;
    }
  }

  get filteredAgents(): AiAgent[] {
    const term = normalizeSearch(this.agentSearch);
    return term ? this.agents.filter(a => normalizeSearch(a.name).includes(term)) : this.agents;
  }

  get agentPageCount(): number {
    return Math.max(1, Math.ceil(this.filteredAgents.length / AGENTS_PAGE_SIZE));
  }

  get pagedAgents(): AiAgent[] {
    const start = (this.agentPage - 1) * AGENTS_PAGE_SIZE;
    return this.filteredAgents.slice(start, start + AGENTS_PAGE_SIZE);
  }

  searchAgents(term: string): void {
    this.agentSearch = term || '';
    this.agentPage = 1;
  }

  setAgentPage(page: number): void {
    this.agentPage = Math.min(Math.max(page, 1), this.agentPageCount);
  }

  /** Abre na página do agente do passo, para ele aparecer marcado. */
  private pageOfSelectedAgent(): number {
    const index = this.filteredAgents.findIndex(a => a.id === this.agentId);
    return index < 0 ? 1 : Math.floor(index / AGENTS_PAGE_SIZE) + 1;
  }

  /**
   * As integrações (números) ligadas ao agente: o número por onde ele responde — ou "qualquer
   * número", se não tem — e os números em que ele é o agente padrão.
   */
  agentIntegrations(agent: AiAgent): AgentIntegrationLine[] {
    const lines: AgentIntegrationLine[] = [];
    const own = agent.meta_whatsapp_account_id
      ? (this.accounts.find(a => a.id === agent.meta_whatsapp_account_id) || agent.whatsapp_account)
      : null;

    if (own) {
      lines.push({ name: own.name, phone: own.display_phone_number, note: 'responde por este número' });
    } else {
      lines.push({ name: 'Sem número fixo', note: 'responde pelo número de cada automação' });
    }

    this.accounts
      .filter(a => a.default_ai_agent_id === agent.id)
      .forEach(a => {
        const line = lines.find(l => l.name === a.name);
        if (line && own && a.id === own.id) {
          line.note = 'responde por este número · agente padrão';
        } else {
          lines.push({ name: a.name, phone: a.display_phone_number, note: 'agente padrão do número' });
        }
      });

    return lines;
  }

  get selectedAgent(): AiAgent | null {
    return this.agents.find(a => a.id === this.agentId) || null;
  }

  toggleAi(): void {
    if (!this.aiAvailable) {
      return;
    }
    this.ai = !this.ai;

    // Um agente só no número (e ativo): ligar a IA já escolhe ele.
    if (this.ai && !this.agentId && this.agents.length === 1 && this.isAgentSelectable(this.agents[0])) {
      this.agentId = this.agents[0].id;
    }
  }

  promptExcerpt(agent: AiAgent): string {
    const prompt = agent.base_prompt || '';
    return prompt.length > 70 ? prompt.substring(0, 70) + '…' : prompt;
  }

  /**
   * "Criar um agente novo" ou, com `source`, "Copiar agente": o mesmo formulário curto, com a
   * conversa de teste. Só cria ao confirmar; Cancelar não cria nada.
   */
  openQuickAgent(source: AiAgent | null = null): void {
    const ref = this.modalService.open(QuickAgentModalComponent, { size: 'xl', scrollable: true });
    ref.componentInstance.account = this.selectedAccount;
    ref.componentInstance.source = source;
    ref.componentInstance.context = 'step';
    ref.result.then(
      (agent: AiAgent) => {
        if (!agent) {
          return;
        }
        this.agents = [...this.agents.filter(a => a.id !== agent.id), agent];
        this.agentId = agent.id;
        this.ai = true;
        this.agentSearch = '';
        this.agentPage = this.pageOfSelectedAgent();
        this.toastService.success(source
          ? `Cópia “${agent.name}” criada e selecionada neste passo.`
          : `Agente “${agent.name}” criado e selecionado neste passo.`);
      },
      () => { /* fechou sem criar */ }
    );
  }

  // ------------------------------------------------------------------ passo 5 · resumo

  get intervalTypeName(): string {
    return (this.intervalTypes || []).find(t => t.id === Number(this.intervalType))?.name?.toLowerCase() || '';
  }

  get followupTypeName(): string {
    return Number(this.followupWindowType) === HOUR_INTERVAL ? 'horas' : 'minutos';
  }

  // ------------------------------------------------------------------ navegação e validação

  /** O que impede o passo de seguir. Lista vazia = passo ok. */
  stepErrors(step: number): string[] {
    const errors: string[] = [];

    if (step === 1) {
      if (!this.accountId) {
        errors.push('Escolha por qual número este passo envia.');
      }
    }

    if (step === 2) {
      if (!this.templateName) {
        errors.push('Escolha o template aprovado que abre a conversa.');
      }
      // Pelas variáveis do template, não pelo que estava salvo: o template pode ter mudado na Meta.
      // Sem a lista de templates (não carregou), vale o que estava salvo.
      if (this.templateName && this.selectedTemplate) {
        const empty = this.slots.find(slot => !mappingSource(this.templateVariables[slot.key]).value.trim());
        if (empty) {
          errors.push(`Escolha o dado do lead ou escreva o texto da variável ${empty.label} do template.`);
        }
        const badMedia = this.slots.find(slot => this.isMediaSlot(slot) && this.templateVariables[slot.key]
          && !/^https:\/\/\S+\.\S+/i.test(mappingSource(this.templateVariables[slot.key]).value.trim()));
        if (badMedia) {
          errors.push(`Informe o link (https://…) da mídia do cabeçalho do template.`);
        }
      } else if (this.templateName && this.mappingNormalized) {
        const emptyKey = Object.keys(this.templateVariables).find(key => !mappingSource(this.templateVariables[key]).value.trim());
        if (emptyKey) {
          errors.push(`Escolha o dado do lead ou escreva o texto da variável {{${emptyKey.split(':')[1]}}} do template.`);
        }
      }
      // Com a lista da Meta em mãos, o template tem de estar aprovado com o mesmo nome E idioma:
      // pausado, rejeitado ou apagado faria todo envio do passo ser recusado. Ele continua
      // aparecendo marcado, com aviso (Bruno, 09/10); sem a lista (token), não dá para conferir.
      if (this.templateName && !this.loadingTemplates && !this.templatesError && this.savedTemplateMissing) {
        errors.push('O template salvo não está mais aprovado na Meta. Escolha um template aprovado.');
      }
    }

    if (step === 3) {
      if (this.followup) {
        if (!(this.text || '').trim()) {
          errors.push('Escreva a mensagem ou desligue o envio após a resposta.');
        }
        if ((this.text || '').length > WHATSAPP_TEXT_MAX) {
          errors.push(`A mensagem passa de ${WHATSAPP_TEXT_MAX} caracteres.`);
        }
        // As mesmas checagens do form antigo para o texto livre: palavra bloqueada e tag que
        // este passo não preenche (review de 09/10 12:15).
        const blocked = this.blockedWord(this.text);
        if (blocked) {
          errors.push(blocked);
        }
        const invalidTags = this.text ? this.sequencesService.validateTags({ text: this.text } as Sequence, this.tags) : [];
        if (invalidTags.length) {
          errors.push(`Tag que este passo não preenche: ${invalidTags.join(', ')}.`);
        }
        if (this.followupWindowInMinutes < 1) {
          errors.push('Informe o prazo para a resposta do lead.');
        }
        if (this.followupWindowInMinutes > FOLLOWUP_MAX_MINUTES) {
          errors.push('O prazo para a resposta do lead não pode passar de 23h59.');
        }
      }
      if (this.needsLink && !/^https?:\/\/\S+\.\S+/i.test((this.url || '').trim())) {
        errors.push('Preencha o Meu link com uma URL válida (https://…).');
      }
    }

    if (step === 4) {
      if (this.ai && this.loadingAgents) {
        errors.push('Carregando os agentes do número…');
      } else if (this.ai && this.agentsError) {
        errors.push('Não foi possível carregar os agentes. Tente de novo.');
      } else if (this.ai && !this.selectedAgent) {
        errors.push('Escolha o agente que responde ou desligue a IA.');
      } else if (this.ai && !this.selectedAgent.active && this.selectedAgent.id !== this.sequence?.ai_agent_id) {
        errors.push('Este agente está inativo. Escolha um agente ativo ou ative-o em Meus Agentes.');
      }
    }

    if (step === 5) {
      if (this.interval === null || this.interval === undefined || `${this.interval}` === '' || Number(this.interval) < 0) {
        errors.push('Informe quando enviar após a entrada na lista.');
      }
    }

    return errors;
  }

  showErrors(step: number): boolean {
    return !!this.attempted[step] && this.stepErrors(step).length > 0;
  }

  next(): void {
    this.attempted[this.step] = true;
    if (this.stepErrors(this.step).length) {
      return;
    }

    // Veio do resumo: volta para ele — a não ser que a mudança tenha deixado outro passo pendente
    // (trocar o número limpa o template e o agente), que abre antes.
    if (this.returnToSummary) {
      const pending = [1, 2, 3, 4].find(s => this.stepErrors(s).length);
      if (pending) {
        this.attempted[pending] = true;
        this.setStep(pending);
        return;
      }
      this.setStep(SUMMARY_STEP);
      return;
    }

    this.setStep(Math.min(this.step + 1, this.steps.length));
  }

  back(): void {
    this.setStep(Math.max(this.step - 1, 1));
  }

  /**
   * Passos clicáveis no topo: na edição, todos; na criação, os já abertos e o seguinte, se os
   * anteriores estão ok (Bruno, 09/10).
   */
  canGoTo(target: number): boolean {
    if (this.isEditing || target <= this.furthestStep) {
      return true;
    }
    if (target > this.furthestStep + 1) {
      return false;
    }
    for (let s = 1; s < target; s++) {
      if (this.stepErrors(s).length) {
        return false;
      }
    }
    return true;
  }

  goTo(target: number): void {
    if (!this.canGoTo(target) || target === this.step) {
      return;
    }
    // Do resumo para um passo: o "Continuar" de lá volta ao resumo.
    if (this.step === SUMMARY_STEP && target < SUMMARY_STEP) {
      this.returnToSummary = true;
    }
    this.setStep(target);
  }

  /** Lápis de um bloco do resumo. "Quando" está no próprio resumo: o lápis põe o foco no campo. */
  editFromSummary(target: number): void {
    if (target === SUMMARY_STEP) {
      (document.getElementById('wa-interval') as HTMLInputElement | null)?.focus();
      return;
    }
    this.returnToSummary = true;
    this.setStep(target);
  }

  private setStep(target: number): void {
    this.step = target;
    this.furthestStep = Math.max(this.furthestStep, target);
    if (target === SUMMARY_STEP) {
      this.returnToSummary = false;
    }
  }

  buildPayload(): any {
    return {
      id: this.id || undefined,
      campaign_id: this.campaign?.id,
      active: this.active,
      sequence_type: 'whatsapp',
      meta_whatsapp_account_id: this.accountId,
      template_name: this.templateName,
      template_language: this.templateLanguage,
      template_variables: this.payloadVariables(),
      // Desligado = só o template. Zero e não nulo: é a escolha explícita de não enviar.
      text: this.followup ? this.text : null,
      followup_window: this.followup ? Number(this.followupWindow) : 0,
      followup_window_type_id: Number(this.followupWindowType),
      url: this.needsLink ? this.url.trim() : null,
      send_lead_number: this.needsLink ? this.sendLeadNumber : false,
      // Com a IA desligada na conta, o agente gravado fica no passo (o envio não liga a IA sem ela)
      // e volta a responder quando a IA for liberada — editar o intervalo não apaga o vínculo.
      ai_agent_id: this.aiAvailable
        ? (this.ai && this.selectedAgent ? this.agentId : null)
        : (this.keepsSavedAgent ? this.sequence?.ai_agent_id ?? null : null),
      interval: Number(this.interval),
      interval_type_id: Number(this.intervalType),
      is_ac: false,
      ac_tags: [],
      voxuy_campaign_url: null,
      voxuy_campaign_description: null,
      call4u_campaign_url: null,
      call4u_campaign_description: null,
      call4u_audio_id: null,
    };
  }

  save(): void {
    for (let s = 1; s <= this.steps.length; s++) {
      if (this.stepErrors(s).length) {
        this.attempted[s] = true;
        this.step = s;
        return;
      }
    }

    this.saving = true;
    this.saveError = null;
    this.subscriptions.push(
      this.sequencesService.save(this.buildPayload(), ownErrorToastOptions()).subscribe({
        next: saved => {
          this.saving = false;
          const agent = this.ai && this.selectedAgent ? ` · IA ${this.selectedAgent.name}` : '';
          this.toastService.success(
            `Mensagem de WhatsApp ${this.isEditing ? 'atualizada' : 'adicionada'}: ${this.selectedAccount?.name || 'número'} · ${this.templateName}${agent}.`
          );
          this.activeModal.close(saved || true);
        },
        error: res => {
          this.saving = false;
          this.saveError = this.errorMessage(res);
        },
      })
    );
  }

  /**
   * Só as variáveis que o template tem (a Meta recusa parâmetro a mais), como mapa vaga → valor.
   * Sem a lista de templates, o passo antigo segue com o que tinha — não se perde a lista salva.
   */
  private payloadVariables(): { [key: string]: string } | string[] {
    if (this.selectedTemplate) {
      const out: { [key: string]: string } = {};
      this.slots.forEach(slot => {
        if (this.templateVariables[slot.key]) {
          out[slot.key] = this.templateVariables[slot.key];
        }
      });
      return out;
    }
    if (!this.mappingNormalized && Array.isArray(this.savedMapping)) {
      return [...this.savedMapping];
    }
    return { ...this.templateVariables };
  }

  /** O aviso do modal é o único (o interceptor não dá o toast: `ownErrorToastOptions`). */
  private errorMessage(res: any): string | null {
    return ownErrorMessage(res, 'Não foi possível salvar o passo de WhatsApp. Tente de novo.');
  }

  /** Palavra bloqueada no texto, pelo mesmo validador do form antigo. Sem a lista carregada, passa. */
  private blockedWord(text: string): string | null {
    try {
      return this.blacklistValidator.validate(text)?.message || null;
    } catch {
      return null;
    }
  }

  private fromSequence(sequence: Sequence): void {
    this.id = sequence.id || null;
    this.active = sequence.active !== false;
    this.accountId = sequence.meta_whatsapp_account_id || sequence.whatsapp_account?.id || null;
    this.templateName = sequence.template_name || null;
    this.templateLanguage = sequence.template_language || null;
    this.savedMapping = sequence.template_variables;
    if (isLegacyMapping(sequence.template_variables)) {
      // Lista antiga (ou vazia): vira mapa quando o template do número carregar.
      this.templateVariables = {};
      this.mappingNormalized = false;
    } else {
      this.templateVariables = { ...(sequence.template_variables as { [key: string]: string }) };
    }
    this.text = sequence.text || '';
    this.followup = !!this.text && Number(sequence.followup_window) !== 0;
    this.followupWindow = sequence.followup_window ?? FOLLOWUP_DEFAULT_WINDOW;
    this.followupWindowType = sequence.followup_window_type_id || HOUR_INTERVAL;
    this.url = sequence.url || '';
    this.sendLeadNumber = !!sequence.send_lead_number;
    this.agentId = sequence.ai_agent_id || null;
    // Com a IA desligada na conta o passo 4 não tem o switch: o agente antigo não pode travar o salvar.
    this.ai = !!this.agentId && this.aiAvailable;
    this.interval = sequence.interval ?? 0;
    this.intervalType = sequence.interval_type_id || MINUTE_INTERVAL;
  }
}
